import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const DMVProcessesPage = () => {
  const [activeService, setActiveService] = useState('drivers-license');
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada DMV Processes & Requirements Las Vegas - Step-by-Step Guide',
    description: 'Detailed step-by-step guide to all Nevada DMV processes in Las Vegas. Vehicle registration, title transfer, license plates, temporary permits, and duplicate titles. Speedy DMV Services at 702.979.7202.',
    keywords: 'Nevada DMV process Las Vegas, DMV requirements Nevada, vehicle registration process Nevada, title transfer steps Las Vegas',
    canonical: `${siteUrl}/dmv-processes`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Nevada DMV Services Process Guide",
          "description": "Step-by-step guide for completing Nevada DMV services including vehicle registration, title transfer, and more.",
          "url": `${siteUrl}/dmv-processes`
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'DMV Processes', url: '/dmv-processes' }
        ])
      ]
    }
  });

  const services = [
    { id: 'drivers-license', name: "Driver's License & ID", icon: 'ri-id-card-line' },
    { id: 'vehicle-registration', name: 'Vehicle Registration', icon: 'ri-car-line' },
    { id: 'title-transfer', name: 'Title Transfer', icon: 'ri-file-transfer-line' },
    { id: 'license-plates', name: 'License Plates', icon: 'ri-license-line' },
    { id: 'temporary-permits', name: 'Temporary Permits', icon: 'ri-time-line' },
    { id: 'duplicate-title', name: 'Duplicate Title', icon: 'ri-file-copy-line' }
  ];

  const processData = {
    'drivers-license': {
      title: "Driver's License & ID Services",
      description: "Complete guide to Nevada driver's license and identification card processes through Speedy DMV Services.",
      requirements: [
        "Proof of identity (birth certificate, valid passport, or certified naturalization papers)",
        "Social Security number verification",
        "Two proofs of Nevada residency (utility bills, bank statements, lease agreement)",
        "Vision screening results",
        "Completed application form",
        "Payment for applicable fees"
      ],
      process: [
        {
          step: 1,
          title: "Schedule Appointment",
          description: "Call (702) 979-7202 to schedule your appointment at our Las Vegas location."
        },
        {
          step: 2,
          title: "Gather Required Documents",
          description: "Collect all necessary identification and residency documents listed in requirements."
        },
        {
          step: 3,
          title: "Complete Application",
          description: "Fill out the Nevada driver license application form before your visit."
        },
        {
          step: 4,
          title: "Visit Our Office",
          description: "Bring all documents to 4525 S Sandhill Rd, Las Vegas, NV for processing."
        },
        {
          step: 5,
          title: "Processing & Payment",
          description: "We'll review documents, process your application, and collect the service fee."
        },
        {
          step: 6,
          title: "DMV Submission",
          description: "Your application is submitted to Nevada DMV for final approval and issuance."
        }
      ],
      fees: "Starting at $120 (includes DMV fees + service fee)",
      timeline: "Same-day processing, license arrives by mail in 7-10 business days"
    },
    'vehicle-registration': {
      title: "Vehicle Registration Services",
      description: "Fast and efficient Nevada vehicle registration services for new and used vehicles.",
      requirements: [
        "Vehicle title or manufacturer's certificate of origin",
        "Nevada insurance verification",
        "Emissions inspection (if required for your area)",
        "VIN inspection (for out-of-state vehicles)",
        "Previous registration (if applicable)",
        "Lien documentation (if financed)"
      ],
      process: [
        {
          step: 1,
          title: "Insurance Verification",
          description: "Ensure your Nevada auto insurance is active and meets state requirements."
        },
        {
          step: 2,
          title: "Schedule Appointment",
          description: "Call (702) 979-7202 to book your vehicle registration appointment."
        },
        {
          step: 3,
          title: "Emissions & VIN Check",
          description: "Complete required inspections before your appointment if applicable."
        },
        {
          step: 4,
          title: "Document Review",
          description: "We'll verify all paperwork and calculate exact fees before submission."
        },
        {
          step: 5,
          title: "DMV Processing",
          description: "Submit your registration to Nevada DMV with all required documentation."
        },
        {
          step: 6,
          title: "Receive Registration",
          description: "Get your registration card and license plates (if needed) the same day."
        }
      ],
      fees: "Starting at $120 (varies by vehicle type and weight)",
      timeline: "Same-day service, immediate registration and plates"
    },
    'title-transfer': {
      title: "Title Transfer Services",
      description: "Complete Nevada vehicle title transfer services for purchased, inherited, or gifted vehicles.",
      requirements: [
        "Current vehicle title (properly signed by seller)",
        "Bill of sale with purchase price",
        "Nevada insurance verification",
        "Odometer disclosure statement",
        "Emissions inspection (if required)",
        "Lien release (if applicable)"
      ],
      process: [
        {
          step: 1,
          title: "Document Collection",
          description: "Gather the signed title, bill of sale, and all supporting documents."
        },
        {
          step: 2,
          title: "Insurance Setup",
          description: "Obtain Nevada auto insurance coverage for the vehicle."
        },
        {
          step: 3,
          title: "Schedule Service",
          description: "Call (702) 979-7202 to schedule your title transfer appointment."
        },
        {
          step: 4,
          title: "Document Verification",
          description: "We'll review all paperwork for completeness and accuracy."
        },
        {
          step: 5,
          title: "Fee Calculation",
          description: "Calculate all DMV fees, taxes, and our service fee."
        },
        {
          step: 6,
          title: "Title Processing",
          description: "Submit to Nevada DMV and receive temporary documents same day."
        }
      ],
      fees: "Starting at $100 (plus applicable taxes and DMV fees)",
      timeline: "Same-day processing, new title arrives by mail in 10-15 days"
    },
    'license-plates': {
      title: "License Plate Services",
      description: "New license plates, specialty plates, and plate replacement services.",
      requirements: [
        "Current vehicle registration",
        "Nevada insurance verification",
        "Photo identification",
        "Specialty plate application (if applicable)",
        "Personalized plate request form (if custom)"
      ],
      process: [
        {
          step: 1,
          title: "Plate Selection",
          description: "Choose standard, specialty, or personalized license plates."
        },
        {
          step: 2,
          title: "Availability Check",
          description: "For personalized plates, we'll verify your choice is available."
        },
        {
          step: 3,
          title: "Schedule Appointment",
          description: "Call (702) 979-7202 to schedule your plate service."
        },
        {
          step: 4,
          title: "Document Review",
          description: "Verify registration and insurance information."
        },
        {
          step: 5,
          title: "Order Processing",
          description: "Submit plate order to Nevada DMV with payment."
        },
        {
          step: 6,
          title: "Plate Issuance",
          description: "Receive standard plates same day or track specialty plate delivery."
        }
      ],
      fees: "Starting at $45 (specialty plates have additional fees)",
      timeline: "Standard plates: same day, Specialty plates: 2-4 weeks"
    },
    'temporary-permits': {
      title: "Temporary Movement Permits",
      description: "Temporary permits for newly purchased vehicles and out-of-state moves.",
      requirements: [
        "Vehicle purchase documentation or current registration",
        "Nevada insurance verification (for in-state moves)",
        "Valid driver's license",
        "VIN verification"
      ],
      process: [
        {
          step: 1,
          title: "Determine Permit Type",
          description: "Choose between 10-day (Nevada vehicles) or 30-day (out-of-state) permits."
        },
        {
          step: 2,
          title: "Insurance Verification",
          description: "Ensure proper insurance coverage is in place."
        },
        {
          step: 3,
          title: "Quick Service",
          description: "Visit our office at 4525 S Sandhill Rd for immediate permit issuance."
        },
        {
          step: 4,
          title: "Document Processing",
          description: "We'll verify your information and process the permit application."
        },
        {
          step: 5,
          title: "Permit Issuance",
          description: "Receive your temporary permit immediately upon approval."
        }
      ],
      fees: "Starting at $85 (varies by permit type and duration)",
      timeline: "Immediate issuance, valid for 10 or 30 days"
    },
    'duplicate-title': {
      title: "Duplicate Title Services",
      description: "Replacement titles for lost, stolen, or damaged Nevada vehicle titles.",
      requirements: [
        "Valid photo identification",
        "Vehicle identification number (VIN)",
        "Current registration information",
        "Lien information (if applicable)",
        "Notarized affidavit (for some situations)"
      ],
      process: [
        {
          step: 1,
          title: "Verify Ownership",
          description: "Confirm your identity and vehicle ownership through our verification process."
        },
        {
          step: 2,
          title: "Schedule Appointment",
          description: "Call (702) 979-7202 to schedule your duplicate title service."
        },
        {
          step: 3,
          title: "Complete Application",
          description: "Fill out the duplicate title application with vehicle details."
        },
        {
          step: 4,
          title: "Document Submission",
          description: "Submit application with required identification and fees."
        },
        {
          step: 5,
          title: "DMV Processing",
          description: "We'll submit your request to Nevada DMV for processing."
        },
        {
          step: 6,
          title: "Title Delivery",
          description: "Receive your duplicate title by mail within 10-15 business days."
        }
      ],
      fees: "Starting at $100 (includes DMV fees + service fee)",
      timeline: "Same-day processing, title arrives by mail in 10-15 days"
    }
  };

  const faqs = [
    {
      id: 'appointment',
      question: "Do I need an appointment?",
      answer: "Yes, appointments are required for all services. Call (702) 979-7202 to schedule. This ensures we can provide you with dedicated time and avoid waiting."
    },
    {
      id: 'same-day',
      question: "Can services be completed the same day?",
      answer: "Most services are processed the same day. Registration, plates, and permits are typically completed immediately. Licenses and titles are processed same-day but arrive by mail."
    },
    {
      id: 'documents',
      question: "What if I'm missing documents?",
      answer: "We'll review your situation and help determine alternatives. Some documents can be obtained during your visit, while others may require rescheduling."
    },
    {
      id: 'fees',
      question: "What payment methods do you accept?",
      answer: "We accept cash, checks, and all major credit/debit cards. All fees include both DMV costs and our professional service fee."
    },
    {
      id: 'out-of-state',
      question: "Do you help with out-of-state transfers?",
      answer: "Yes! We specialize in helping new Nevada residents transfer their out-of-state licenses and vehicle registrations quickly and efficiently."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Nevada DMV Processes & Requirements
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete guide to all DMV services available through Speedy DMV Services. 
              Skip the DMV lines and get professional assistance with all your Nevada DMV needs.
            </p>
          </div>
        </div>
      </div>

      {/* Service Selection */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Select a Service</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setActiveService(service.id)}
                  className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    activeService === service.id
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <i className={`${service.icon} text-2xl`}></i>
                    <span className="font-medium">{service.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Service Details */}
        {activeService && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Overview */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {processData[activeService as keyof typeof processData].title}
                </h2>
                <p className="text-gray-600 mb-6">
                  {processData[activeService as keyof typeof processData].description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-teal-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-2">Service Fee</h4>
                    <p className="text-teal-700">{processData[activeService as keyof typeof processData].fees}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Processing Time</h4>
                    <p className="text-blue-700">{processData[activeService as keyof typeof processData].timeline}</p>
                  </div>
                </div>
              </div>

              {/* Process Steps */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Step-by-Step Process</h3>
                <div className="space-y-6">
                  {processData[activeService as keyof typeof processData].process.map((step) => (
                    <div key={step.step} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Required Documents */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Required Documents</h3>
                <div className="space-y-3">
                  {processData[activeService as keyof typeof processData].requirements.map((req, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <i className="ri-check-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span className="text-gray-700">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="bg-teal-500 text-white rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Ready to Get Started?</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="ri-phone-line text-xl"></i>
                    <div>
                      <p className="font-medium">Call for Appointment</p>
                      <p className="text-teal-100">(702) 979-7202</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="ri-map-pin-line text-xl mt-1"></i>
                    <div>
                      <p className="font-medium">Visit Our Office</p>
                      <p className="text-teal-100">4525 S Sandhill Rd<br />Las Vegas, NV</p>
                    </div>
                  </div>
                </div>
                <Link
                  to="/appointment"
                  className="block w-full bg-white text-teal-600 text-center py-3 px-4 rounded-lg font-medium mt-6 hover:bg-gray-50 transition-colors"
                >
                  Schedule Appointment
                </Link>
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Services</h3>
                <div className="space-y-3">
                  <Link to="/fees" className="block text-teal-600 hover:text-teal-700 font-medium">
                    <i className="ri-money-dollar-circle-line mr-2"></i>
                    View All Fees
                  </Link>
                  <Link to="/required-documents" className="block text-teal-600 hover:text-teal-700 font-medium">
                    <i className="ri-file-list-line mr-2"></i>
                    Document Checklist
                  </Link>
                  <Link to="/location" className="block text-teal-600 hover:text-teal-700 font-medium">
                    <i className="ri-map-pin-line mr-2"></i>
                    Office Location
                  </Link>
                  <Link to="/faq" className="block text-teal-600 hover:text-teal-700 font-medium">
                    <i className="ri-question-line mr-2"></i>
                    FAQ
                  </Link>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Office Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-medium">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday</span>
                    <span className="font-medium">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sunday</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  📞 Appointments Required - Please Call Ahead
                </p>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${openFAQ === faq.id ? 'up' : 'down'}-s-line text-gray-500`}></i>
                  </button>
                  {openFAQ === faq.id && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-teal-500 to-blue-600 rounded-lg p-8 text-white">
            <h2 className="text-3xl font-bold mb-4">Skip the DMV Lines Today</h2>
            <p className="text-xl mb-6 text-teal-100">
              Professional DMV services with appointments available. Call now to get started.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:7029797202"
                className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (702) 979-7202
              </a>
              <Link
                to="/appointment"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors"
              >
                Schedule Online
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DMVProcessesPage;
